import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, useEffect, useState } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { isAlreadyPaidAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { setPlayerRateOnRoundAtom } from "../../../../../../../../../../atoms/actions/setPlayerRateOnRound/setPlayerRateOnRoundAtom";
import { getRoundFromIdAtom } from "../../../../../../../../../../atoms/selectors/getRoundFromIdAtom/getRoundFromIdAtom";
import { DurationPlayerRateControlQuery } from "./__generated__/DurationPlayerRateControlQuery.graphql";

type Props = {
  bookingId: string;
  currentMembershipTypeId: string;
  durationMins: number;
  groupSize: number;
  roundId: string;
};

export const DurationPlayerRateControl = ({
  bookingId,
  currentMembershipTypeId,
  durationMins,
  groupSize,
  roundId,
}: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<DurationPlayerRateControlQuery>(
    DurationPlayerRateControlQueryString
  );

  useEffect(() => {
    if (bookingId && currentMembershipTypeId && durationMins && groupSize) {
      loadQuery({
        bookingId,
        membershipTypeId: currentMembershipTypeId,
        duration: durationMins,
        groupSize,
      });
    }
  }, [bookingId, currentMembershipTypeId, durationMins, groupSize, loadQuery]);

  return (
    <Suspense
      fallback={<SkeletonLoader borderRadius="2" width={341} height={48} />}
    >
      {queryRef ? (
        <DurationPlayerRateControlInner queryRef={queryRef} roundId={roundId} />
      ) : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<DurationPlayerRateControlQuery>;
  roundId: string;
};

const DurationPlayerRateControlInner = ({ queryRef, roundId }: InnerProps) => {
  const { currencySymbol } = useGetClubCurrency();
  const booking = usePreloadedQuery(
    DurationPlayerRateControlQueryString,
    queryRef
  );
  const [isTargetingAll, setIsTargetingAll] = useState(false);
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const isDurationRound = currentRound?.type === "Duration";
  const currentRate = isDurationRound ? currentRound?.rate : null;

  const currentRateId = currentRate?.id ?? "";
  const setPlayerRateOnRound = useSetAtom(setPlayerRateOnRoundAtom);
  const isCurrentRateLocked = isDurationRound
    ? currentRate?.isLocked ?? false
    : false;

  const slot = booking?.node?.startSlot;
  const playerRates = slot?.playerRates;

  const singleOption: DropdownSelectOption<string> = {
    value: currentRateId,
    label: currentRate?.name || "",
    subLabel: `${currencySymbol}${ConvertCentsToDollars(
      currentRate?.totalDurationPrice ?? 0
    )}`,
  };

  const multipleOptions: DropdownSelectOption<string>[] =
    playerRates?.flatMap((rate) => {
      if (!rate?.rate?.id) return [];
      const priceInCents = rate?.totalDurationPrice ?? 0;
      const priceDisplay = ConvertCentsToDollars(priceInCents ?? 0);
      return {
        value: rate.rate.id,
        label: rate.rate.name || "",
        subLabel: `${currencySymbol}${priceDisplay}`,
      };
    }) ?? [];

  const dropdownOptions = isCurrentRateLocked
    ? [singleOption]
    : multipleOptions;

  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);

  return dropdownOptions.length > 0 ? (
    <VStack flex={1}>
      <DropdownSelect
        hideOuterMargin
        isViewOnly={isCurrentRateLocked || isAlreadyPaid}
        hideChevron
        includeLeftElementInSelectedValue
        contentSizeOverride="regular"
        options={dropdownOptions}
        value={currentRateId}
        optionalToggle={{
          initiallyChecked: isTargetingAll,
          onToggle: setIsTargetingAll,
        }}
        onChange={(rateId) => {
          setPlayerRateOnRound({
            roundId,
            rate: { id: rateId },
            isTargetingAll,
          });
        }}
      />
    </VStack>
  ) : null;
};

const DurationPlayerRateControlQueryString = graphql`
  query DurationPlayerRateControlQuery(
    $bookingId: ID!
    $membershipTypeId: ID!
    $duration: Int
    $groupSize: Int
  ) {
    node(id: $bookingId) {
      ... on Booking {
        startSlot {
          id
          ... on TimeSlot {
            playerRates(highestPrecedentOnly: false, type: CUSTOMER) {
              rate {
                id
                name
                visibility
                appliesToMembershipType(membershipTypeId: $membershipTypeId)
                ... on CustomerRate {
                  customerTypes {
                    edges {
                      node {
                        id
                        additionalInfo
                      }
                    }
                  }
                }
              }
              totalDurationPrice(duration: $duration, playerCount: $groupSize)
            }
          }
        }
      }
    }
  }
`;
